import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaAws,
  FaBolt,
  FaChartBar,
  FaCheckCircle,
  FaCodeBranch,
  FaClock,
  FaCloud,
  FaCloudUploadAlt,
  FaDatabase,
  FaDownload,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaFileAlt,
  FaGoogle,
  FaListAlt,
  FaNetworkWired,
  FaRobot,
  FaServer,
  FaShieldAlt,
  FaSitemap,
  FaTimes,
} from "react-icons/fa";
import { jsPDF } from "jspdf";

import { useChat } from "../../../context/contextChat";
import { useDesign } from "../../../context/contextDesign";
import { useAuth } from "../../../context/contextAuth";
import { useAwsConfig } from "../../../context/Dashboard/aws/contextConfig";
import { useAwsIp } from "../../../context/Dashboard/aws/contextIp";
import { useAwsUrl } from "../../../context/Dashboard/aws/contextUrl";
import { useAzureConfig } from "../../../context/Dashboard/azure/contextConfig";
import { useAzureIp } from "../../../context/Dashboard/azure/contextIp";
import { useAzureUrl } from "../../../context/Dashboard/azure/contextUrl";
import { useGcpConfig } from "../../../context/Dashboard/gcp/contextConfig";
import { useGcpIp } from "../../../context/Dashboard/gcp/contextIp";
import { useGcpUrl } from "../../../context/Dashboard/gcp/contextUrl";
import { useOciConfig } from "../../../context/Dashboard/oci/contextConfig";
import { useOciIp } from "../../../context/Dashboard/oci/contextIp";
import { useOciUrl } from "../../../context/Dashboard/oci/contextUrl";
import { scanEc2Ip, scanS3Url } from "../../../api/post/post-aws";
import { scanBlobUrl, scanVmsIp } from "../../../api/post/post-azure";
import { scanGceIp, scanGcsUrl } from "../../../api/post/post-gcp";
import { scanOciIp, scanOciUrl } from "../../../api/post/post-oci";
import {
  CLOUD_SERVICE_CATALOG,
  buildCloudEvidence,
  cloudFindingsCsv,
  downloadTextFile,
} from "./cloudModel";
import "./CloudSecurityCenter.css";

const COMMAND_TABS = [
  { id: "executive", label: "Executive Dashboard", icon: FaChartBar },
  { id: "ai", label: "AI Security Analyst", icon: FaRobot },
  { id: "paths", label: "Attack Scenarios", icon: FaCodeBranch },
  { id: "reports", label: "Reports", icon: FaFileAlt },
  { id: "technical", label: "Technical Analysis", icon: FaListAlt },
];

const VIEW_TABS = [
  { id: "inventory", label: "Asset inventory", icon: FaDatabase },
  { id: "risks", label: "Risk register", icon: FaExclamationTriangle },
  { id: "compliance", label: "Compliance", icon: FaShieldAlt },
  { id: "paths", label: "Attack paths", icon: FaSitemap },
];

const RISK_ORDER = { Critical: 5, High: 4, Medium: 3, Low: 2, Review: 1 };
const RISK_COLORS = {
  Critical: "#ff5c72",
  High: "#ff9b51",
  Medium: "#ffd166",
  Low: "#55d6a0",
  Review: "#7790b2",
};

const parseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatAssessmentDate = (value) => {
  if (!value) return "Not available";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not available"
    : date.toLocaleString("en-IN");
};

const numberFrom = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== null) return parsed;
  }
  return 0;
};

const labelFromKey = (value = "") =>
  String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const hasProviderData = (value) => {
  const parsed = parseJson(value);
  if (Array.isArray(parsed)) return parsed.length > 0;
  return Boolean(
    parsed && typeof parsed === "object" && Object.keys(parsed).length > 0
  );
};

const riskLabel = (level, flagged = 0) => {
  const normalized = String(level || "").toLowerCase();
  if (["danger", "critical"].includes(normalized)) return "Critical";
  if (["warning", "high"].includes(normalized)) return "High";
  if (normalized === "medium") return "Medium";
  if (["low", "info", "informational"].includes(normalized)) return "Low";
  return flagged > 0 ? "Review" : "Low";
};

const normalizeRows = (source, provider) => {
  const parsedSource = parseJson(source);
  const containers = Array.isArray(parsedSource) ? parsedSource : [parsedSource];

  return containers.flatMap((rawContainer, containerIndex) => {
    const container = parseJson(rawContainer);
    if (!container || typeof container !== "object") return [];

    const data = parseJson(container.misconfig ?? container);
    const summary = parseJson(data?.last_run?.summary ?? data?.summary);
    if (!summary || typeof summary !== "object") return [];

    const entries = Array.isArray(summary)
      ? summary.map((item, index) => [
          item?.name || item?.service || `Check ${index + 1}`,
          item,
        ])
      : Object.entries(summary);

    return entries
      .map(([key, rawItem], index) => {
        const item =
          rawItem && typeof rawItem === "object"
            ? rawItem
            : { flagged_items: rawItem };
        const checked = numberFrom(
          item.checked_items,
          item.checkedItems,
          item.checked
        );
        const flagged = numberFrom(
          item.flagged_items,
          item.flaggedItems,
          item.failed,
          item.issues
        );
        const resources = numberFrom(
          item.resources_count,
          item.resourcesCount,
          item.resources
        );
        const rules = numberFrom(item.rules_count, item.rulesCount, item.rules);
        const level = String(item.max_level || item.level || "").toLowerCase();

        return {
          id: `${provider}-${containerIndex}-${key}-${index}`,
          provider,
          name: item.name || item.title || labelFromKey(key),
          service: item.service || item.category || labelFromKey(key),
          checked,
          flagged,
          resources,
          rules,
          level,
          severity: riskLabel(level, flagged),
        };
      })
      .filter(
        (row) => row.checked || row.flagged || row.resources || row.rules
      );
  });
};

const candidateAssessmentDate = (source, assetsSource) => {
  const assets = Array.isArray(assetsSource) ? assetsSource : [];
  const data = parseJson(source);
  const candidates = [
    assets[0]?.created_at,
    assets[0]?.createdAt,
    assets[0]?.updated_at,
    assets[0]?.updatedAt,
    assets[0]?.scan_time,
    assets[0]?.scanTime,
    data?.last_run?.timestamp,
    data?.last_run?.time,
    data?.last_run?.date,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const date = new Date(candidate);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return null;
};

const dateLabel = (value, includeTime = false) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(value);
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(
    Number(value) || 0
  );

const KpiCard = ({ icon, label, value, helper, tone = "blue" }) => (
  <article className={`csc-kpi csc-kpi--${tone}`}>
    <span className="csc-kpi__icon">{icon}</span>
    <div>
      <span className="csc-kpi__label">{label}</span>
      <strong>{value}</strong>
      <small>{helper}</small>
    </div>
  </article>
);

const EmptyState = ({ icon: Icon = FaCloud, title, children }) => (
  <div className="csc-empty">
    <Icon aria-hidden="true" />
    <div>
      <strong>{title}</strong>
      <span>{children}</span>
    </div>
  </div>
);

const CloudSecurityCenter = ({
  initialProviderScope = "all",
  initialWorkspace = "executive",
  initialTechnicalView = "misconfiguration",
}) => {
  const { bHeightVal } = useDesign();
  const { userId } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setPrompt, setShowChat, openVulnerabilityActions } = useChat();
  const aws = useAwsConfig();
  const awsIp = useAwsIp();
  const awsUrl = useAwsUrl();
  const azure = useAzureConfig();
  const azureIp = useAzureIp();
  const azureUrl = useAzureUrl();
  const gcp = useGcpConfig();
  const gcpIp = useGcpIp();
  const gcpUrl = useGcpUrl();
  const oci = useOciConfig();
  const ociIp = useOciIp();
  const ociUrl = useOciUrl();
  const [providerScope, setProviderScope] = useState(initialProviderScope);
  const [activeWorkspace, setActiveWorkspace] = useState(initialWorkspace);
  const [activeView, setActiveView] = useState("inventory");
  const [technicalView, setTechnicalView] = useState(initialTechnicalView);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showTargetScanPanel, setShowTargetScanPanel] = useState(false);
  const [targetAuthorization, setTargetAuthorization] = useState(false);
  const [selectedTargetKeys, setSelectedTargetKeys] = useState([]);
  const [targetScanState, setTargetScanState] = useState({
    running: false,
    completed: 0,
    total: 0,
    message: "",
    errors: [],
  });
  const [showScanPanel, setShowScanPanel] = useState(false);
  const [scanProvider, setScanProvider] = useState(
    ["aws", "azure", "gcp", "oci"].includes(initialProviderScope)
      ? initialProviderScope
      : "aws"
  );
  const [scanStarted, setScanStarted] = useState(false);

  useEffect(() => {
    [awsIp, awsUrl, azureIp, azureUrl, gcpIp, gcpUrl, ociIp, ociUrl].forEach((context) => {
      Promise.resolve(context?.initData?.()).catch((error) => {
        console.warn("Unable to load a cloud scanner result set:", error);
      });
    });
    // These cloud scanner histories only need to be hydrated once for the
    // unified command center. Their context methods are recreated per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const routeProvider = ["aws", "azure", "gcp", "oci"].find(
      (provider) =>
        pathname === `/dashboard/cloud-dashboard/${provider}` ||
        pathname.startsWith(`/dashboard/cloud-dashboard/${provider}/`)
    );
    const nextScope = routeProvider || initialProviderScope;

    setProviderScope(nextScope);
    if (["aws", "azure", "gcp", "oci"].includes(nextScope)) {
      setScanProvider(nextScope);
    }
  }, [initialProviderScope, pathname]);

  useEffect(() => {
    setActiveWorkspace(initialWorkspace);
    setTechnicalView(initialTechnicalView);
  }, [initialTechnicalView, initialWorkspace]);

  const providers = useMemo(() => {
    const sourceList = [
      {
        id: "aws",
        shortName: "AWS",
        name: "Amazon Web Services",
        Icon: FaAws,
        source: aws?.cAwsConfigAll || aws?.configAssets,
        assetsSource: aws?.configAssets,
        networkResults: awsIp?.networkResults || [],
        webResults: awsUrl?.webResults || [],
        rawScore: aws?.saveScore,
        loading: aws?.configLoading || aws?.configScanLoading,
        error: aws?.error,
        route: "/dashboard/cloud-dashboard/aws",
        availability: "Assessment available",
      },
      {
        id: "azure",
        shortName: "Azure",
        name: "Microsoft Azure",
        Icon: FaCloud,
        source: azure?.cAzureConfigAll || azure?.configAssets,
        assetsSource: azure?.configAssets,
        networkResults: azureIp?.networkResults || [],
        webResults: azureUrl?.webResults || [],
        rawScore: azure?.saveScore,
        loading: azure?.configLoading || azure?.configScanLoading,
        error: azure?.error,
        route: "/dashboard/cloud-dashboard/azure",
        availability: "Assessment available",
      },
      {
        id: "gcp",
        shortName: "GCP",
        name: "Google Cloud Platform",
        Icon: FaCloud,
        source: gcp?.cGcpConfigAll || gcp?.configAssets,
        assetsSource: gcp?.configAssets,
        networkResults: gcpIp?.networkResults || [],
        webResults: gcpUrl?.webResults || [],
        rawScore: gcp?.saveScore,
        loading: gcp?.configLoading || gcp?.configScanLoading,
        error: gcp?.error,
        route: "/dashboard/cloud-dashboard/gcp",
        availability: "Assessment available",
      },
      {
        id: "oci",
        shortName: "OCI",
        name: "Oracle Cloud Infrastructure",
        Icon: FaCloud,
        source: oci?.cOciConfigAll || oci?.configAssets,
        assetsSource: oci?.configAssets,
        networkResults: ociIp?.networkResults || [],
        webResults: ociUrl?.webResults || [],
        rawScore: oci?.saveScore,
        loading: oci?.configLoading || oci?.configScanLoading,
        error: oci?.error,
        route: "/dashboard/cloud-dashboard/oci",
        availability: "Assessment available",
      },
    ];

    return sourceList.map((provider) => {
      const evidence = buildCloudEvidence({
        providerId: provider.id,
        configSource: provider.source,
        networkResults: provider.networkResults,
        webResults: provider.webResults,
      });
      const connected =
        hasProviderData(provider.source) ||
        hasProviderData(provider.assetsSource) ||
        evidence.findings.length > 0;
      const rows = normalizeRows(provider.source, provider.shortName);
      const rowAssetCount = rows.reduce(
        (total, row) => total + row.resources,
        0
      );
      const fallbackAssetCount = Array.isArray(provider.assetsSource)
        ? provider.assetsSource.length
        : 0;
      const parsedScore = connected ? toNumber(provider.rawScore) : null;

      return {
        ...provider,
        evidence,
        connected,
        rows,
        assetCount:
          evidence.assets.length || rowAssetCount || fallbackAssetCount,
        findingCount: rows.reduce(
          (total, row) => total + row.flagged,
          0
        ),
        checkedCount: rows.reduce(
          (total, row) => total + row.checked,
          0
        ),
        score:
          parsedScore === null ? null : Math.min(10, Math.max(0, parsedScore)),
        assessedAt: candidateAssessmentDate(
          provider.source,
          provider.assetsSource
        ),
      };
    });
  }, [
    aws,
    awsIp?.networkResults,
    awsUrl?.webResults,
    azure,
    azureIp?.networkResults,
    azureUrl?.webResults,
    gcp,
    gcpIp?.networkResults,
    gcpUrl?.webResults,
    oci,
    ociIp?.networkResults,
    ociUrl?.webResults,
  ]);

  const scopedProviders = useMemo(
    () =>
      providerScope === "all"
        ? providers
        : providers.filter((provider) => provider.id === providerScope),
    [providerScope, providers]
  );
  const allRows = useMemo(
    () => scopedProviders.flatMap((provider) => provider.rows),
    [scopedProviders]
  );
  const cloudAssets = useMemo(
    () => scopedProviders.flatMap((provider) => provider.evidence.assets),
    [scopedProviders]
  );
  const detailedFindings = useMemo(
    () =>
      scopedProviders
        .flatMap((provider) => provider.evidence.findings)
        .sort(
          (a, b) =>
            RISK_ORDER[b.severity] - RISK_ORDER[a.severity] ||
            b.flagged - a.flagged
        ),
    [scopedProviders]
  );
  const complianceControls = useMemo(
    () => scopedProviders.flatMap((provider) => provider.evidence.compliance),
    [scopedProviders]
  );
  const attackPaths = useMemo(
    () => scopedProviders.flatMap((provider) => provider.evidence.attackPaths),
    [scopedProviders]
  );
  const discoveredTargets = useMemo(
    () => ({
      publicIps: [
        ...new Set(
          scopedProviders.flatMap(
            (provider) => provider.evidence.targets.publicIps
          )
        ),
      ],
      endpoints: [
        ...new Set(
          scopedProviders.flatMap(
            (provider) => provider.evidence.targets.endpoints
          )
        ),
      ],
    }),
    [scopedProviders]
  );
  const targetCandidates = useMemo(
    () =>
      scopedProviders.flatMap((provider) => [
        ...provider.evidence.targets.publicIps.map((target) => ({
          key: `${provider.id}:network:${target}`,
          providerId: provider.id,
          provider: provider.shortName,
          type: "network",
          target,
        })),
        ...provider.evidence.targets.endpoints.map((target) => ({
          key: `${provider.id}:web:${target}`,
          providerId: provider.id,
          provider: provider.shortName,
          type: "web",
          target,
        })),
      ]),
    [scopedProviders]
  );
  const riskRows = useMemo(
    () =>
      allRows
        .filter((row) => row.flagged > 0)
        .sort(
          (a, b) =>
            RISK_ORDER[b.severity] - RISK_ORDER[a.severity] ||
            b.flagged - a.flagged
        ),
    [allRows]
  );

  const connectedProviders = scopedProviders.filter(
    (provider) => provider.connected
  );
  const scores = scopedProviders
    .map((provider) => provider.score)
    .filter((score) => score !== null);
  const overallScore = scores.length
    ? scores.reduce((total, score) => total + score, 0) / scores.length
    : null;
  const scoreTone =
    overallScore === null
      ? "unknown"
      : overallScore >= 8
        ? "good"
        : overallScore >= 6
          ? "watch"
          : overallScore >= 4
            ? "high"
            : "critical";
  const postureLabel =
    overallScore === null
      ? "Not calculated"
      : overallScore >= 8
        ? "Low risk"
        : overallScore >= 6
          ? "Moderate risk"
          : overallScore >= 4
            ? "High risk"
            : "Critical risk";
  const scorePercent = overallScore === null ? 0 : overallScore * 10;
  const totalAssets = scopedProviders.reduce(
    (total, provider) => total + provider.assetCount,
    0
  );
  const totalFindings = scopedProviders.reduce(
    (total, provider) => total + provider.findingCount,
    0
  );
  const totalChecks = scopedProviders.reduce(
    (total, provider) => total + provider.checkedCount,
    0
  );
  const criticalFindings = riskRows
    .filter((row) => row.severity === "Critical")
    .reduce((total, row) => total + row.flagged, 0);
  const assessedDates = scopedProviders
    .map((provider) => provider.assessedAt)
    .filter(Boolean);
  const lastAssessment = assessedDates.length
    ? new Date(Math.max(...assessedDates.map((date) => date.getTime())))
    : null;
  const isLoading = scopedProviders.some((provider) => provider.loading);
  const providerErrors = scopedProviders.filter((provider) => {
    if (!provider.error) return false;
    if (typeof provider.error === "object") {
      return Boolean(provider.error.message);
    }
    return Boolean(provider.error);
  });
  const selectedScope =
    providerScope === "all"
      ? "All cloud providers"
      : providers.find((provider) => provider.id === providerScope)?.name;
  const topRisk = riskRows[0] || null;
  const selectedAssetProvider = selectedAsset
    ? providers.find((provider) => provider.id === selectedAsset.providerId)
    : null;
  const selectedAssetFindings = selectedAsset
    ? detailedFindings.filter(
        (finding) =>
          finding.providerId === selectedAsset.providerId &&
          (finding.service.toLowerCase() === selectedAsset.service.toLowerCase() ||
            finding.evidencePaths.some((path) =>
              path.includes(selectedAsset.resourceId)
            ) ||
            selectedAsset.publicIps.includes(finding.target) ||
            selectedAsset.endpoints.includes(finding.target))
      )
    : [];

  const riskData = Object.keys(RISK_COLORS).map((name) => ({
    name,
    value: riskRows
      .filter((row) => row.severity === name)
      .reduce((total, row) => total + row.flagged, 0),
  }));
  const hasRiskData = riskData.some((entry) => entry.value > 0);
  const chartData = scopedProviders.map((provider) => ({
    name: provider.shortName,
    Assets: provider.assetCount,
    Findings: provider.findingCount,
  }));
  const scanContext = {
    aws,
    azure,
    gcp,
    oci,
  }[scanProvider] || aws;
  const scanLoading = Boolean(scanContext?.configScanLoading);
  const scanProgress = scanContext?.configScanProgress;
  const scanProgressValue = Math.max(
    0,
    Math.min(100, Number(scanProgress?.progress || (scanLoading ? 1 : 0)))
  );
  const scanError =
    typeof scanContext?.error === "object"
      ? scanContext?.error?.message
      : scanContext?.error;
  const scanSetupUrl =
    typeof scanContext?.error === "object"
      ? scanContext?.error?.setupUrl
      : "";
  const scanReady = scanProvider === "azure"
    ? Boolean(azure?.clientId && azure?.clientSecret && azure?.tenantId)
    : scanProvider === "gcp"
      ? Boolean(gcp?.projectId && gcp?.serviceAccountKey)
      : scanProvider === "oci"
        ? Boolean(
            oci?.tenancyId &&
              oci?.ociUserId &&
              oci?.fingerprint &&
              oci?.region &&
              oci?.privateKey
          )
      : Boolean(aws?.accessKey && aws?.secretKey);

  const askAi = (question) => {
    const context = connectedProviders.length
      ? `${selectedScope}; ${totalAssets} observed resources, ${totalFindings} flagged checks, ${criticalFindings} critical findings.`
      : `${selectedScope}; no imported assessment data is currently available.`;
    setPrompt(
      `${question}\n\nUse this current SAVE cloud posture context only: ${context} Do not assume facts that are not present in the assessment evidence.`
    );
    setShowChat(true);
  };

  const openScanWorkflow = () => {
    const supportedScope = ["aws", "azure", "gcp", "oci"].includes(providerScope)
      ? providerScope
      : "aws";
    setScanProvider(supportedScope);
    setScanStarted(false);
    setShowScanPanel(true);
  };

  const openTargetScanWorkflow = () => {
    const initialTargets = targetCandidates.slice(0, 10).map((target) => target.key);
    setSelectedTargetKeys(initialTargets);
    setTargetAuthorization(false);
    setTargetScanState({
      running: false,
      completed: 0,
      total: initialTargets.length,
      message: "",
      errors: [],
    });
    setShowTargetScanPanel(true);
  };

  const runDiscoveredTargetScans = async () => {
    const selectedTargets = targetCandidates.filter((target) =>
      selectedTargetKeys.includes(target.key)
    );
    if (!userId || !targetAuthorization || !selectedTargets.length) return;

    const scanners = {
      aws: { network: scanEc2Ip, web: scanS3Url },
      azure: { network: scanVmsIp, web: scanBlobUrl },
      gcp: { network: scanGceIp, web: scanGcsUrl },
      oci: { network: scanOciIp, web: scanOciUrl },
    };
    const errors = [];
    setTargetScanState({
      running: true,
      completed: 0,
      total: selectedTargets.length,
      message: `Starting ${selectedTargets.length} authorized target scan(s)`,
      errors: [],
    });

    for (let index = 0; index < selectedTargets.length; index += 1) {
      const target = selectedTargets[index];
      setTargetScanState((current) => ({
        ...current,
        message: `Scanning ${target.provider} ${target.type} target ${target.target}`,
      }));
      try {
        await scanners[target.providerId][target.type](userId, target.target);
      } catch (error) {
        errors.push({
          key: target.key,
          target: target.target,
          message: error?.message || "Scan failed",
        });
      }
      setTargetScanState((current) => ({
        ...current,
        completed: index + 1,
        errors: [...errors],
      }));
    }

    await Promise.allSettled(
      [awsIp, awsUrl, azureIp, azureUrl, gcpIp, gcpUrl, ociIp, ociUrl].map(
        (context) => context?.initData?.()
      )
    );
    setTargetScanState((current) => ({
      ...current,
      running: false,
      message: errors.length
        ? `Finished with ${errors.length} target error(s)`
        : "All selected target scans finished",
      errors,
    }));
  };

  const handleAssessmentSubmit = async (event) => {
    event.preventDefault();
    setScanStarted(true);
    await scanContext.handleConfigScan(event);
  };

  const openFinding = (finding) => {
    setSelectedFinding(finding);
  };

  const askAiAboutFinding = (finding) => {
    openVulnerabilityActions(finding);
  };

  const exportFindingsCsv = () => {
    downloadTextFile(
      cloudFindingsCsv(detailedFindings),
      `SAVE_Cloud_Findings_${providerScope}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      "text/csv;charset=utf-8"
    );
  };

  const exportEvidenceJson = () => {
    const evidence = {
      generatedAt: new Date().toISOString(),
      scope: selectedScope,
      summary: {
        saveScore: overallScore,
        assets: totalAssets,
        findings: detailedFindings.length,
        criticalFindings,
        complianceControls: complianceControls.length,
        attackPaths: attackPaths.length,
      },
      providers: scopedProviders.map((provider) => ({
        id: provider.id,
        assessedAt: provider.assessedAt,
        score: provider.score,
        services: provider.rows,
      })),
      assets: cloudAssets.map(({ raw, ...asset }) => asset),
      findings: detailedFindings,
      complianceControls,
      attackPaths: attackPaths.map(({ finding, ...path }) => ({
        ...path,
        findingId: finding.id,
      })),
    };
    downloadTextFile(
      JSON.stringify(evidence, null, 2),
      `SAVE_Cloud_Evidence_${providerScope}_${new Date()
        .toISOString()
        .slice(0, 10)}.json`,
      "application/json;charset=utf-8"
    );
  };

  const exportExecutivePdf = () => {
    const document = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = document.internal.pageSize.getWidth();
    const margin = 42;
    let y = 50;
    const addLine = (text, options = {}) => {
      const lines = document.splitTextToSize(String(text), pageWidth - margin * 2);
      const lineHeight = options.lineHeight || 15;
      if (y + lines.length * lineHeight > 790) {
        document.addPage();
        y = 50;
      }
      document.setFont("helvetica", options.bold ? "bold" : "normal");
      document.setFontSize(options.size || 10);
      document.text(lines, margin, y);
      y += lines.length * lineHeight + (options.gap ?? 5);
    };

    addLine("SAVE Cloud Security Executive Report", { bold: true, size: 18, lineHeight: 22, gap: 12 });
    addLine(`Scope: ${selectedScope}`);
    addLine(`Generated: ${new Date().toLocaleString("en-IN")}`);
    addLine(`SAVE Score: ${overallScore === null ? "Not calculated" : `${overallScore.toFixed(1)}/10`}`);
    addLine(`Cloud assets: ${totalAssets} | Detailed findings: ${detailedFindings.length} | Critical: ${criticalFindings}`);
    addLine(`Evidence-based compliance controls affected: ${complianceControls.length} | Attack paths: ${attackPaths.length}`, { gap: 14 });
    addLine("Priority findings", { bold: true, size: 13, lineHeight: 18, gap: 8 });
    if (!detailedFindings.length) addLine("No detailed finding evidence is currently available.");
    detailedFindings.slice(0, 30).forEach((finding, index) => {
      addLine(`${index + 1}. [${finding.severity}] ${finding.provider} / ${finding.service} — ${finding.findingName}`, { bold: true });
      addLine(`Affected evidence: ${finding.flagged}; Type: ${finding.findingType}; Target: ${finding.target || "provider configuration"}`);
      addLine(`Recommended action: ${finding.remediation}`, { gap: 9 });
    });
    document.save(`SAVE_Cloud_Executive_${providerScope}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportTicketPayload = (finding, system) => {
    const payload = system === "jira"
      ? {
          fields: {
            summary: `[${finding.severity}] ${finding.provider} ${finding.findingName}`,
            description: `${finding.rationale}\n\nRemediation: ${finding.remediation}\n\nEvidence: ${finding.evidencePaths.join(" | ")}`,
            labels: ["SAVE", "cloud-security", finding.providerId, finding.findingType.toLowerCase().replace(/\s+/g, "-")],
            priority: { name: finding.severity },
          },
        }
      : {
          short_description: `[${finding.severity}] ${finding.provider} ${finding.findingName}`,
          description: finding.rationale,
          remediation: finding.remediation,
          severity: finding.severity,
          affected_target: finding.target || finding.service,
          evidence: finding.evidencePaths,
          source: "SAVE Cloud Security Command Center",
        };
    downloadTextFile(
      JSON.stringify(payload, null, 2),
      `SAVE_${system}_${finding.id}.json`,
      "application/json;charset=utf-8"
    );
  };

  const renderInventory = () => (
    <div className="csc-table-wrap">
      {cloudAssets.length ? (
        <table className="csc-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Service</th>
              <th>Asset</th>
              <th>Type</th>
              <th>Region</th>
              <th>Exposure</th>
            </tr>
          </thead>
          <tbody>
            {cloudAssets.map((asset) => (
              <tr
                className="csc-clickable-row"
                key={asset.id}
                onClick={() => setSelectedAsset(asset)}
              >
                <td>
                  <span className="csc-provider-badge">{asset.provider}</span>
                </td>
                <td>
                  <strong>{asset.service}</strong>
                </td>
                <td>
                  <strong>{asset.name}</strong>
                  <small className="csc-table-subline">{asset.resourceId}</small>
                </td>
                <td>{asset.type}</td>
                <td>{asset.region}</td>
                <td>
                  <span
                    className={
                      asset.exposed
                        ? "csc-flag-count csc-flag-count--active"
                        : "csc-flag-count"
                    }
                  >
                    {asset.exposed ? "Public evidence" : "No public target"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : allRows.length ? (
        <table className="csc-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Service</th>
              <th>Resources</th>
              <th>Rules</th>
              <th>Checks</th>
              <th>Flagged</th>
            </tr>
          </thead>
          <tbody>
            {allRows.map((row) => (
              <tr key={row.id}>
                <td><span className="csc-provider-badge">{row.provider}</span></td>
                <td><strong>{row.service}</strong></td>
                <td>{formatNumber(row.resources)}</td>
                <td>{formatNumber(row.rules)}</td>
                <td>{formatNumber(row.checked)}</td>
                <td>{formatNumber(row.flagged)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState title="No cloud inventory is available">
          Run an AWS, Azure, GCP, or OCI assessment to populate verified service
          and resource evidence.
        </EmptyState>
      )}
    </div>
  );

  const renderRisks = () => (
    <div className="csc-table-wrap">
      {detailedFindings.length ? (
        <table className="csc-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Finding</th>
              <th>Type</th>
              <th>Severity</th>
              <th>Affected</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {detailedFindings.map((finding) => (
              <tr
                className="csc-clickable-row"
                key={finding.id}
                onClick={() => openFinding(finding)}
              >
                <td>
                  <span className="csc-provider-badge">{finding.provider}</span>
                </td>
                <td>
                  <strong>{finding.findingName}</strong>
                  <small className="csc-table-subline">{finding.service}</small>
                </td>
                <td>{finding.findingType}</td>
                <td>
                  <span
                    className={`csc-risk-badge csc-risk-badge--${finding.severity.toLowerCase()}`}
                  >
                    {finding.severity}
                  </span>
                </td>
                <td>{formatNumber(finding.flagged)}</td>
                <td>{finding.target || "Configuration"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState icon={FaShieldAlt} title="No flagged checks are available">
          This means no finding evidence was returned; it is not confirmation
          that every cloud resource is secure.
        </EmptyState>
      )}
    </div>
  );

  const renderCompliance = () => (
    <div className="csc-table-wrap">
      {complianceControls.length ? (
        <table className="csc-table">
          <thead>
            <tr>
              <th>Framework</th>
              <th>Control</th>
              <th>Status</th>
              <th>Failed evidence</th>
              <th>Providers</th>
              <th>Services</th>
            </tr>
          </thead>
          <tbody>
            {complianceControls.map((control) => (
              <tr key={`${control.framework}-${control.version}-${control.control}`}>
                <td>
                  <strong>{control.framework}</strong>
                  {control.version && <small className="csc-table-subline">Version {control.version}</small>}
                </td>
                <td>{control.control}</td>
                <td><span className="csc-risk-badge csc-risk-badge--critical">Failed</span></td>
                <td>{formatNumber(control.failedFindings)}</td>
                <td>{control.providers.join(", ")}</td>
                <td>{control.services.join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState icon={FaShieldAlt} title="No framework evidence was returned">
          SAVE only shows controls explicitly mapped by the provider assessment. It does not infer a pass when control evidence is absent.
        </EmptyState>
      )}
    </div>
  );

  const renderAttackPaths = () => (
    <div className="csc-path-list">
      {attackPaths.length ? (
        attackPaths.map((path) => (
          <article key={path.id} className="csc-path-card">
            <div className="csc-path-card__head">
              <span className="csc-provider-badge">{path.provider}</span>
              <span className={`csc-risk-badge csc-risk-badge--${path.severity.toLowerCase()}`}>
                {path.severity}
              </span>
              <strong>{path.title}</strong>
            </div>
            <div className="csc-path-card__steps">
              {path.steps.map((step, index) => (
                <React.Fragment key={`${path.id}-${step}-${index}`}>
                  <span>{step}</span>
                  {index < path.steps.length - 1 && <FaCodeBranch />}
                </React.Fragment>
              ))}
            </div>
            <div className="csc-path-card__footer">
              <small>{path.evidence.length} evidence reference(s)</small>
              <button type="button" onClick={() => openFinding(path.finding)}>
                Review evidence
              </button>
            </div>
          </article>
        ))
      ) : (
        <EmptyState icon={FaSitemap} title="No evidence-backed attack path is available">
          Paths appear only when provider evidence or an existing SAVE network/web scan identifies an exposed target.
        </EmptyState>
      )}
    </div>
  );

  const renderAiWorkspace = () => (
    <section className="csc-card csc-workspace-panel">
      <div className="csc-section-heading">
        <div>
          <span className="csc-eyebrow">Evidence-aware assistance</span>
          <h2>AI Security Analyst</h2>
          <p className="csc-section-copy">
            Select a real assessment finding to explain it, validate it, model
            business impact, map compliance, or generate remediation guidance.
          </p>
        </div>
        <button
          type="button"
          className="csc-workspace-action"
          onClick={() => askAi("Create an executive remediation plan for the current cloud evidence.")}
        >
          <FaRobot /> Analyze full posture
        </button>
      </div>
      <div className="csc-finding-grid">
        {detailedFindings.length ? (
          detailedFindings.slice(0, 60).map((finding) => (
            <article className="csc-finding-card" key={finding.id}>
              <div>
                <span className="csc-provider-badge">{finding.provider}</span>
                <span className={`csc-risk-badge csc-risk-badge--${finding.severity.toLowerCase()}`}>
                  {finding.severity}
                </span>
              </div>
              <small>{finding.findingType} · {finding.service}</small>
              <strong>{finding.findingName}</strong>
              <p>{finding.rationale}</p>
              <div className="csc-finding-card__actions">
                <button type="button" onClick={() => openFinding(finding)}>View evidence</button>
                <button type="button" onClick={() => askAiAboutFinding(finding)}>
                  <FaRobot /> Ask SAVE AI
                </button>
              </div>
            </article>
          ))
        ) : (
          <EmptyState icon={FaRobot} title="No finding evidence is ready for AI analysis">
            Run a cloud assessment or an authorized network/web scan first. AI does not create scanner findings.
          </EmptyState>
        )}
      </div>
    </section>
  );

  const renderReportsWorkspace = () => (
    <section className="csc-card csc-workspace-panel">
      <div className="csc-section-heading">
        <div>
          <span className="csc-eyebrow">Export Center</span>
          <h2>Cloud security reports</h2>
          <p className="csc-section-copy">
            Export executive, technical, and machine-readable evidence for the
            current provider scope. Secret credentials are excluded.
          </p>
        </div>
        <Link className="csc-report-center-link" to="/dashboard/reports">
          <FaExternalLinkAlt /> Open centralized Reports
        </Link>
      </div>
      <div className="csc-report-grid">
        <article>
          <FaFileAlt />
          <strong>Executive PDF</strong>
          <p>Score, asset and risk summary, compliance impact, attack paths, and priority fixes.</p>
          <button type="button" onClick={exportExecutivePdf}><FaDownload /> Download PDF</button>
        </article>
        <article>
          <FaListAlt />
          <strong>Technical findings CSV</strong>
          <p>Normalized configuration, network, and web findings for engineering workflows.</p>
          <button type="button" onClick={exportFindingsCsv} disabled={!detailedFindings.length}>
            <FaDownload /> Download CSV
          </button>
        </article>
        <article>
          <FaDatabase />
          <strong>Evidence JSON</strong>
          <p>Provider summaries, assets, controls, findings, and attack-path provenance.</p>
          <button type="button" onClick={exportEvidenceJson}><FaDownload /> Download JSON</button>
        </article>
      </div>
      <div className="csc-report-summary">
        <div><span>Providers</span><strong>{connectedProviders.length}</strong></div>
        <div><span>Assets</span><strong>{totalAssets}</strong></div>
        <div><span>Detailed findings</span><strong>{detailedFindings.length}</strong></div>
        <div><span>Mapped controls</span><strong>{complianceControls.length}</strong></div>
        <div><span>Attack paths</span><strong>{attackPaths.length}</strong></div>
      </div>
    </section>
  );

  const renderTechnicalWorkspace = () => {
    const typeLabel = {
      misconfiguration: "Misconfiguration",
      network: "Network Vulnerability",
      web: "Web Vulnerability",
    }[technicalView];
    const filteredFindings = detailedFindings.filter((finding) => finding.findingType === typeLabel);
    const moduleProviders = providerScope === "all"
      ? scopedProviders
      : scopedProviders.filter((provider) => provider.id === providerScope);

    return (
      <section className="csc-card csc-workspace-panel">
        <div className="csc-section-heading csc-section-heading--stack">
          <div>
            <span className="csc-eyebrow">Provider parity</span>
            <h2>Technical Analysis</h2>
            <p className="csc-section-copy">
              The same three analysis modes are available for AWS, Azure, GCP,
              and OCI. Scanner results are merged without duplicating engines.
            </p>
          </div>
          <nav className="csc-tabs" aria-label="Cloud technical analysis views">
            {[
              ["misconfiguration", "Misconfiguration", FaShieldAlt],
              ["network", "Network Vulnerabilities", FaNetworkWired],
              ["web", "Web Vulnerabilities", FaCloud],
            ].map(([id, label, Icon]) => (
              <button
                type="button"
                className={technicalView === id ? "is-active" : ""}
                key={id}
                onClick={() => setTechnicalView(id)}
              >
                <Icon /> {label}
              </button>
            ))}
          </nav>
        </div>
        {technicalView === "misconfiguration" && (
          <div className="csc-service-modules">
            {moduleProviders.flatMap((provider) =>
              (CLOUD_SERVICE_CATALOG[provider.id] || []).map((service) => {
                const normalizedService = service.toLowerCase();
                const row = provider.rows.find((item) =>
                  item.service.toLowerCase().includes(normalizedService) ||
                  normalizedService.includes(item.service.toLowerCase())
                );
                const findings = provider.evidence.findings.filter(
                  (finding) =>
                    finding.findingType === "Misconfiguration" &&
                    (finding.service.toLowerCase().includes(normalizedService) ||
                      normalizedService.includes(finding.service.toLowerCase()))
                );
                return (
                  <article key={`${provider.id}-${service}`}>
                    <span className="csc-provider-badge">{provider.shortName}</span>
                    <strong>{service}</strong>
                    <small>{row?.resources || 0} resources · {findings.length} rules failed</small>
                    <button
                      type="button"
                      disabled={!findings.length}
                      onClick={() => findings[0] && openFinding(findings[0])}
                    >
                      {findings.length ? "Review findings" : "No finding evidence"}
                    </button>
                  </article>
                );
              })
            )}
          </div>
        )}
        {technicalView !== "misconfiguration" && (
          <div className="csc-technical-scanner">
            <div className="csc-target-summary">
              <div><span>Discovered public IPs</span><strong>{discoveredTargets.publicIps.length}</strong></div>
              <div><span>Discovered HTTP endpoints</span><strong>{discoveredTargets.endpoints.length}</strong></div>
              <div><span>Imported scanner findings</span><strong>{filteredFindings.length}</strong></div>
            </div>
            <div className="csc-target-actions">
              <div>
                <strong>Run existing SAVE scanners</strong>
                <span>
                  Review discovered targets and confirm that you are authorized
                  before a network or web scan starts.
                </span>
              </div>
              <button
                type="button"
                onClick={openTargetScanWorkflow}
                disabled={!targetCandidates.length || targetScanState.running}
              >
                <FaShieldAlt /> Review targets and scan
              </button>
            </div>
            {filteredFindings.length ? (
              <div className="csc-table-wrap">
                <table className="csc-table">
                  <thead><tr><th>Provider</th><th>Target</th><th>Finding</th><th>Severity</th><th>CVSS</th><th>EPSS</th></tr></thead>
                  <tbody>
                    {filteredFindings.map((finding) => (
                      <tr className="csc-clickable-row" key={finding.id} onClick={() => openFinding(finding)}>
                        <td><span className="csc-provider-badge">{finding.provider}</span></td>
                        <td>{finding.target}</td>
                        <td><strong>{finding.findingName}</strong></td>
                        <td><span className={`csc-risk-badge csc-risk-badge--${finding.severity.toLowerCase()}`}>{finding.severity}</span></td>
                        <td>{finding.cvss ?? "—"}</td>
                        <td>{finding.epss ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon={FaNetworkWired} title={`No ${technicalView} scan results are available`}>
                Discovery targets are shown only from provider evidence. Run an authorized scan through the existing SAVE scanner to populate this view.
              </EmptyState>
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="csc-shell" style={{ height: bHeightVal }}>
      <main className="csc-page">
        <header className="csc-header">
          <div>
            <span className="csc-eyebrow">Cloud Security Operations</span>
            <h1>Cloud Security Command Center</h1>
            <p>
              Unified visibility across cloud providers, assets,
              misconfigurations, and response priorities.
            </p>
          </div>
          <div className="csc-header__actions">
            <label className="csc-scope-select">
              <span>Cloud scope</span>
              <select
                value={providerScope}
                onChange={(event) => {
                  const nextScope = event.target.value;
                  setProviderScope(nextScope);
                  navigate(
                    nextScope === "all"
                      ? "/dashboard/cloud-dashboard"
                      : `/dashboard/cloud-dashboard/${nextScope}`
                  );
                }}
              >
                <option value="all">All providers</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.shortName}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="csc-scan-trigger"
              onClick={openScanWorkflow}
            >
              <FaCloudUploadAlt />
              Run assessment
            </button>
            <Link
              className="csc-legacy-link"
              to={`${pathname}?cloudDashboard=legacy`}
            >
              Legacy dashboard
            </Link>
          </div>
        </header>

        <nav className="csc-command-tabs" aria-label="Cloud command center sections">
          {COMMAND_TABS.map(({ id, label, icon: Icon }) => (
            <button
              type="button"
              key={id}
              className={activeWorkspace === id ? "is-active" : ""}
              onClick={() => setActiveWorkspace(id)}
            >
              <Icon /> {label}
            </button>
          ))}
        </nav>

        {showScanPanel && (
          <section className="csc-card csc-scan-panel">
            <div className="csc-section-heading">
              <div>
                <span className="csc-eyebrow">Authorized Cloud Assessment</span>
                <h2>Run a cloud misconfiguration scan</h2>
                <p className="csc-section-copy">
                  Choose a supported provider and supply temporary assessment
                  credentials. The existing ScoutSuite workflow will collect
                  posture evidence and refresh this dashboard.
                </p>
              </div>
              {!scanLoading && (
                <button
                  type="button"
                  className="csc-icon-button"
                  onClick={() => setShowScanPanel(false)}
                  aria-label="Close cloud assessment"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <div
              className="csc-scan-provider-toggle"
              aria-label="Assessment provider"
            >
              <button
                type="button"
                className={scanProvider === "aws" ? "is-active" : ""}
                onClick={() => {
                  setScanProvider("aws");
                  setScanStarted(false);
                }}
                disabled={scanLoading}
              >
                <FaAws /> Amazon Web Services
              </button>
              <button
                type="button"
                className={scanProvider === "azure" ? "is-active" : ""}
                onClick={() => {
                  setScanProvider("azure");
                  setScanStarted(false);
                }}
                disabled={scanLoading}
              >
                <FaCloud /> Microsoft Azure
              </button>
              <button
                type="button"
                className={scanProvider === "gcp" ? "is-active" : ""}
                onClick={() => {
                  setScanProvider("gcp");
                  setScanStarted(false);
                }}
                disabled={scanLoading}
              >
                <FaGoogle /> Google Cloud Platform
              </button>
              <button
                type="button"
                className={scanProvider === "oci" ? "is-active" : ""}
                onClick={() => {
                  setScanProvider("oci");
                  setScanStarted(false);
                }}
                disabled={scanLoading}
              >
                <FaCloud /> Oracle Cloud Infrastructure
              </button>
            </div>

            <form className="csc-scan-form" onSubmit={handleAssessmentSubmit}>
              {scanProvider === "aws" && (
                <>
                  <label className="csc-scan-field">
                    <span>AWS access key ID</span>
                    <input
                      type="text"
                      value={aws?.accessKey || ""}
                      onChange={(event) => aws?.setAccessKey(event.target.value)}
                      placeholder="Enter a temporary assessment access key"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>AWS secret access key</span>
                    <input
                      type="password"
                      value={aws?.secretKey || ""}
                      onChange={(event) => aws?.setSecretKey(event.target.value)}
                      placeholder="Enter the matching secret key"
                      autoComplete="new-password"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                </>
              )}
              {scanProvider === "azure" && (
                <>
                  <label className="csc-scan-field">
                    <span>Azure client ID</span>
                    <input
                      type="text"
                      value={azure?.clientId || ""}
                      onChange={(event) => azure?.setClientId(event.target.value)}
                      placeholder="Enter the application client ID"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>Azure tenant ID</span>
                    <input
                      type="text"
                      value={azure?.tenantId || ""}
                      onChange={(event) => azure?.setTenantId(event.target.value)}
                      placeholder="Enter the directory tenant ID"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>Azure client secret</span>
                    <input
                      type="password"
                      value={azure?.clientSecret || ""}
                      onChange={(event) =>
                        azure?.setClientSecret(event.target.value)
                      }
                      placeholder="Enter the application client secret"
                      autoComplete="new-password"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                </>
              )}
              {scanProvider === "gcp" && (
                <>
                  <label className="csc-scan-field">
                    <span>GCP project ID</span>
                    <input
                      type="text"
                      value={gcp?.projectId || ""}
                      onChange={(event) => gcp?.setProjectId(event.target.value)}
                      placeholder="Enter the target Google Cloud project ID"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field csc-scan-field--file">
                    <span>GCP service account key</span>
                    <input
                      type="file"
                      accept=".json,application/json"
                      onChange={gcp?.handleServiceAccountFile}
                      disabled={scanLoading}
                      aria-describedby="gcp-key-status"
                    />
                    <small
                      id="gcp-key-status"
                      className={gcp?.serviceAccountFileName ? "is-ready" : ""}
                    >
                      {gcp?.serviceAccountFileName
                        ? `${gcp.serviceAccountFileName} selected`
                        : "Select the downloaded service-account JSON key file"}
                    </small>
                  </label>
                </>
              )}
              {scanProvider === "oci" && (
                <>
                  <label className="csc-scan-field">
                    <span>OCI tenancy OCID</span>
                    <input
                      type="text"
                      value={oci?.tenancyId || ""}
                      onChange={(event) => oci?.setTenancyId(event.target.value)}
                      placeholder="ocid1.tenancy.oc1…"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>OCI user OCID</span>
                    <input
                      type="text"
                      value={oci?.ociUserId || ""}
                      onChange={(event) => oci?.setOciUserId(event.target.value)}
                      placeholder="ocid1.user.oc1…"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>OCI API-key fingerprint</span>
                    <input
                      type="text"
                      value={oci?.fingerprint || ""}
                      onChange={(event) => oci?.setFingerprint(event.target.value)}
                      placeholder="aa:bb:cc:…"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>OCI region</span>
                    <input
                      type="text"
                      value={oci?.region || ""}
                      onChange={(event) => oci?.setRegion(event.target.value)}
                      placeholder="For example, ap-mumbai-1"
                      autoComplete="off"
                      disabled={scanLoading}
                      required
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>OCI compartment OCID (optional)</span>
                    <input
                      type="text"
                      value={oci?.compartmentId || ""}
                      onChange={(event) =>
                        oci?.setCompartmentId(event.target.value)
                      }
                      placeholder="Defaults to the tenancy"
                      autoComplete="off"
                      disabled={scanLoading}
                    />
                  </label>
                  <label className="csc-scan-field">
                    <span>Private-key passphrase (optional)</span>
                    <input
                      type="password"
                      value={oci?.passphrase || ""}
                      onChange={(event) => oci?.setPassphrase(event.target.value)}
                      placeholder="Only for an encrypted API key"
                      autoComplete="new-password"
                      disabled={scanLoading}
                    />
                  </label>
                  <label className="csc-scan-field csc-scan-field--file">
                    <span>OCI API signing private key</span>
                    <input
                      type="file"
                      accept=".pem,.key,application/x-pem-file"
                      onChange={oci?.handlePrivateKeyFile}
                      disabled={scanLoading}
                      aria-describedby="oci-key-status"
                    />
                    <small
                      id="oci-key-status"
                      className={oci?.privateKeyFileName ? "is-ready" : ""}
                    >
                      {oci?.privateKeyFileName
                        ? `${oci.privateKeyFileName} selected`
                        : "Select the private PEM key matching the OCI fingerprint"}
                    </small>
                  </label>
                </>
              )}

              {scanLoading && (
                <div
                  className="csc-scan-progress"
                  role="progressbar"
                  aria-label={`${scanProvider.toUpperCase()} assessment progress`}
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={scanProgressValue}
                >
                  <div className="csc-scan-progress__header">
                    <span>
                      {scanProgress?.message ||
                        `Scanning ${scanProvider.toUpperCase()} cloud services`}
                    </span>
                    <strong>{scanProgressValue}%</strong>
                  </div>
                  <div className="csc-scan-progress__track">
                    <span
                      style={{ width: `${scanProgressValue}%` }}
                    />
                  </div>
                  <small>
                    Keep this page open. Large cloud environments can take
                    several minutes to assess.
                  </small>
                </div>
              )}

              <div className="csc-scan-submit">
                <div>
                  {scanError && (
                    <span className="csc-scan-message is-error">
                      <FaExclamationTriangle />
                      <span>{scanError}</span>
                      {scanSetupUrl && (
                        <a
                          href={scanSetupUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open Google Cloud API setup
                        </a>
                      )}
                    </span>
                  )}
                  {!scanError && scanStarted && !scanLoading && (
                    <span className="csc-scan-message is-success">
                      <FaCheckCircle /> Assessment finished. Updated evidence is
                      loading into the dashboard.
                    </span>
                  )}
                  {!scanStarted && (
                    <span className="csc-scan-message">
                      Use a least-privilege assessment identity. Secret fields
                      are cleared after submission.
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={!scanReady || scanLoading}
                >
                  <FaCloudUploadAlt />
                  {scanLoading
                    ? `Scanning ${scanProvider.toUpperCase()}…`
                    : `Start ${scanProvider.toUpperCase()} assessment`}
                </button>
              </div>
            </form>
          </section>
        )}

        {providerErrors.length > 0 && (
          <div className="csc-alert" role="alert">
            <FaExclamationTriangle />
            Some provider data could not be loaded. Available assessment
            evidence is shown without inferred replacements.
          </div>
        )}

        <div hidden={activeWorkspace !== "executive"}>
          <section
            className="csc-kpi-grid"
            aria-label="Cloud security key performance indicators"
          >
          <KpiCard
            icon={<FaShieldAlt />}
            label="Security Score"
            value={
              overallScore === null ? "—" : `${overallScore.toFixed(1)}/10`
            }
            helper={postureLabel}
            tone={scoreTone}
          />
          <KpiCard
            icon={<FaCloud />}
            label="Connected Clouds"
            value={
              providerScope === "all"
                ? `${connectedProviders.length}/4`
                : connectedProviders.length
                  ? "Connected"
                  : "—"
            }
            helper={
              connectedProviders.length
                ? "Assessment data imported"
                : "No provider data"
            }
            tone="good"
          />
          <KpiCard
            icon={<FaServer />}
            label="Cloud Assets"
            value={totalAssets || "—"}
            helper="Observed resources"
          />
          <KpiCard
            icon={<FaExclamationTriangle />}
            label="Flagged Findings"
            value={connectedProviders.length ? totalFindings : "—"}
            helper={
              connectedProviders.length
                ? `${formatNumber(totalChecks)} checks assessed`
                : "Awaiting assessment"
            }
            tone="high"
          />
          <KpiCard
            icon={<FaBolt />}
            label="Critical Risks"
            value={connectedProviders.length ? criticalFindings : "—"}
            helper={
              connectedProviders.length
                ? `${riskRows.length} affected services`
                : "No finding evidence"
            }
            tone="critical"
          />
          <KpiCard
            icon={<FaClock />}
            label="Last Assessment"
            value={dateLabel(lastAssessment)}
            helper={
              lastAssessment ? "Latest imported result" : "Timestamp unavailable"
            }
            tone="cyan"
          />
          <KpiCard
            icon={<FaCheckCircle />}
            label="Compliance"
            value={complianceControls.length || "—"}
            helper={
              complianceControls.length
                ? "Evidence-mapped controls affected"
                : "No framework evidence returned"
            }
            tone="purple"
          />
          <KpiCard
            icon={<FaRobot />}
            label="AI Confidence"
            value="—"
            helper="No confidence API connected"
          />
          </section>

        <section className="csc-command-grid">
          <article className="csc-card csc-score-card">
            <div className="csc-section-heading">
              <div>
                <span className="csc-eyebrow">Live Cloud Posture</span>
                <h2>Overall SAVE score</h2>
              </div>
              <span
                className={`csc-status-pill ${
                  connectedProviders.length ? "" : "is-idle"
                }`}
              >
                <i />{" "}
                {isLoading
                  ? "Synchronizing"
                  : connectedProviders.length
                    ? selectedScope
                    : "Awaiting assessment"}
              </span>
            </div>
            <div
              className={`csc-score-orb csc-score-orb--${scoreTone}`}
              style={{ "--score": `${scorePercent}%` }}
            >
              <div className="csc-score-orb__mesh" />
              <div className="csc-score-orb__content">
                <span>Cloud posture</span>
                <strong>
                  {overallScore === null ? "—" : overallScore.toFixed(1)}
                </strong>
                <small>{postureLabel}</small>
              </div>
            </div>
            <div className="csc-score-meta">
              <div>
                <span>Last assessed</span>
                <strong>{dateLabel(lastAssessment, true)}</strong>
              </div>
              <div>
                <span>Flagged checks</span>
                <strong>
                  {connectedProviders.length
                    ? formatNumber(totalFindings)
                    : "—"}
                </strong>
              </div>
            </div>
          </article>

          <article className="csc-card csc-ai-card">
            <div className="csc-section-heading">
              <div>
                <span className="csc-eyebrow">SAVE Intelligence</span>
                <h2>Cloud security copilot</h2>
              </div>
              <FaRobot className="csc-heading-icon" />
            </div>
            <div className="csc-ai-status">
              <span>Cloud security status</span>
              <strong>
                {connectedProviders.length
                  ? `${formatNumber(totalFindings)} flagged checks require review`
                  : "Run an AWS, Azure, GCP, or OCI assessment to establish a baseline"}
              </strong>
            </div>
            <div className="csc-recommendation">
              <FaBolt />
              <div>
                <span>Today&apos;s recommendation</span>
                <strong>
                  {topRisk
                    ? `Review ${topRisk.provider} ${topRisk.name}`
                    : "Connect a supported cloud provider"}
                </strong>
                <p>
                  {topRisk
                    ? `Validate ${topRisk.flagged} flagged ${topRisk.severity.toLowerCase()} checks against the provider evidence before remediation.`
                    : "Import verified assessment evidence. SAVE will not infer risks when provider data is absent."}
                </p>
              </div>
            </div>
            <div className="csc-recent-risks">
              <span>Priority services</span>
              {riskRows.length ? (
                riskRows.slice(0, 3).map((row) => (
                  <div key={row.id}>
                    <i
                      style={{ background: RISK_COLORS[row.severity] }}
                      aria-hidden="true"
                    />
                    <strong>
                      {row.provider} · {row.name}
                    </strong>
                    <small>{row.flagged} flagged</small>
                  </div>
                ))
              ) : (
                <EmptyState title="No risk evidence yet">
                  Complete a supported provider assessment.
                </EmptyState>
              )}
            </div>
            <div className="csc-ai-questions">
              {[
                "Is my cloud secure?",
                "What should I fix first?",
                "Which services are exposed?",
                "Summarize critical risks.",
              ].map((question) => (
                <button
                  key={question}
                  type="button"
                  onClick={() => askAi(question)}
                >
                  {question}
                </button>
              ))}
            </div>
            <button
              className="csc-ai-input"
              type="button"
              onClick={() =>
                askAi(
                  "Analyze my current cloud security posture and recommend the next evidence-based actions."
                )
              }
            >
              <span>Ask SAVE AI about this cloud posture</span>
              <FaRobot />
            </button>
          </article>

          <aside className="csc-card csc-risk-card">
            <div className="csc-section-heading">
              <div>
                <span className="csc-eyebrow">Risk Profile</span>
                <h2>Finding distribution</h2>
              </div>
            </div>
            <div className="csc-pie-wrap">
              {hasRiskData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskData.filter((entry) => entry.value > 0)}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={54}
                      outerRadius={82}
                      paddingAngle={4}
                    >
                      {riskData
                        .filter((entry) => entry.value > 0)
                        .map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={RISK_COLORS[entry.name]}
                          />
                        ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#091429",
                        border: "1px solid #294465",
                        borderRadius: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState title="No scored findings">
                  Risk distribution starts after an assessment.
                </EmptyState>
              )}
            </div>
            <div className="csc-risk-legend">
              {riskData.map((item) => (
                <div key={item.name}>
                  <i style={{ background: RISK_COLORS[item.name] }} />
                  <span>{item.name}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </aside>
        </section>

        <section className="csc-card csc-provider-section">
          <div className="csc-section-heading">
            <div>
              <span className="csc-eyebrow">Multi-cloud Coverage</span>
              <h2>Provider intelligence</h2>
            </div>
            <span className="csc-section-note">
              {isLoading ? "Synchronizing provider data…" : selectedScope}
            </span>
          </div>
          <div className="csc-provider-grid">
            {providers.map(({ Icon, ...provider }) => {
              const content = (
                <>
                  <span className="csc-provider-icon">
                    <Icon />
                  </span>
                  <div className="csc-provider-copy">
                    <span>{provider.shortName}</span>
                    <strong>{provider.name}</strong>
                    <small>
                      {provider.loading
                        ? "Synchronizing"
                        : provider.connected
                          ? "Assessment connected"
                          : provider.availability}
                    </small>
                  </div>
                  <div className="csc-provider-stats">
                    <span>
                      <strong>{formatNumber(provider.assetCount)}</strong>
                      resources
                    </span>
                    <span>
                      <strong>{formatNumber(provider.findingCount)}</strong>
                      findings
                    </span>
                    <span>
                      <strong>
                        {provider.score === null
                          ? "—"
                          : provider.score.toFixed(1)}
                      </strong>
                      score
                    </span>
                  </div>
                  {provider.route && (
                    <span className="csc-provider-open">
                      Open provider <FaExternalLinkAlt />
                    </span>
                  )}
                </>
              );

              return provider.route ? (
                <Link
                  className={`csc-provider ${
                    provider.connected ? "is-connected" : ""
                  } ${providerScope === provider.id ? "is-selected" : ""}`}
                  key={provider.id}
                  to={provider.route}
                >
                  {content}
                </Link>
              ) : (
                <article
                  className={`csc-provider ${
                    provider.connected ? "is-connected" : ""
                  } ${providerScope === provider.id ? "is-selected" : ""}`}
                  key={provider.id}
                >
                  {content}
                </article>
              );
            })}
          </div>
        </section>

        <section className="csc-insight-grid">
          <article className="csc-card csc-chart-card">
            <div className="csc-section-heading">
              <div>
                <span className="csc-eyebrow">Exposure Intelligence</span>
                <h2>Assets and findings by provider</h2>
              </div>
              <FaChartBar className="csc-heading-icon" />
            </div>
            <p className="csc-section-copy">
              Counts come directly from current provider assessment summaries.
            </p>
            <div className="csc-chart-wrap">
              {chartData.some((item) => item.Assets || item.Findings) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 12, right: 12, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      stroke="#203453"
                      strokeDasharray="3 6"
                    />
                    <XAxis
                      dataKey="name"
                      stroke="#7790b2"
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#7790b2"
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#091429",
                        border: "1px solid #294465",
                        borderRadius: 12,
                      }}
                    />
                    <Bar
                      dataKey="Assets"
                      fill="#4d8dff"
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar
                      dataKey="Findings"
                      fill="#ff9b51"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState title="No provider distribution yet">
                  Complete an AWS, Azure, GCP, or OCI assessment to populate this
                  chart.
                </EmptyState>
              )}
            </div>
          </article>

          <article className="csc-card csc-coverage-card">
            <div className="csc-section-heading">
              <div>
                <span className="csc-eyebrow">Assessment Coverage</span>
                <h2>Evidence health</h2>
              </div>
            </div>
            <div className="csc-coverage-list">
              <div>
                <span>Supported providers assessed</span>
                <strong>
                  {
                    providers.filter(
                      (provider) =>
                        provider.connected &&
                        ["aws", "azure", "gcp", "oci"].includes(provider.id)
                    ).length
                  }
                  /4
                </strong>
              </div>
              <div>
                <span>Normalized service summaries</span>
                <strong>{allRows.length}</strong>
              </div>
              <div>
                <span>Checks evaluated</span>
                <strong>{formatNumber(totalChecks)}</strong>
              </div>
              <div>
                <span>Credential values displayed</span>
                <strong className="is-good">0</strong>
              </div>
            </div>
            <p className="csc-data-note">
              The command center uses posture results only. Provider
              credentials are never shown in this interface.
            </p>
          </article>
        </section>

          <section className="csc-card csc-evidence-card">
          <div className="csc-section-heading csc-section-heading--stack">
            <div>
              <span className="csc-eyebrow">Verified Evidence</span>
              <h2>Cloud assessment workspace</h2>
            </div>
            <nav className="csc-tabs" aria-label="Cloud evidence views">
              {VIEW_TABS.map(({ id, label, icon: Icon }) => (
                <button
                  type="button"
                  className={activeView === id ? "is-active" : ""}
                  key={id}
                  onClick={() => setActiveView(id)}
                >
                  <Icon />
                  {label}
                </button>
              ))}
            </nav>
          </div>
            {activeView === "inventory" && renderInventory()}
            {activeView === "risks" && renderRisks()}
            {activeView === "compliance" && renderCompliance()}
            {activeView === "paths" && renderAttackPaths()}
          </section>
        </div>

        {activeWorkspace === "ai" && renderAiWorkspace()}
        {activeWorkspace === "paths" && (
          <section className="csc-card csc-workspace-panel">
            <div className="csc-section-heading">
              <div>
                <span className="csc-eyebrow">Evidence graph</span>
                <h2>Attack Scenarios</h2>
                <p className="csc-section-copy">
                  Paths are generated only from observed public exposure and
                  existing SAVE scanner findings.
                </p>
              </div>
            </div>
            {renderAttackPaths()}
          </section>
        )}
        {activeWorkspace === "reports" && renderReportsWorkspace()}
        {activeWorkspace === "technical" && renderTechnicalWorkspace()}

        {showTargetScanPanel && (
          <div
            className="csc-detail-backdrop"
            role="presentation"
            onClick={() => {
              if (!targetScanState.running) setShowTargetScanPanel(false);
            }}
          >
            <aside
              className="csc-detail-drawer csc-target-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Authorize discovered cloud target scans"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="csc-detail-drawer__header">
                <div>
                  <span className="csc-provider-badge">SAVE scanners</span>
                  <h2>Review discovered targets</h2>
                  <p>
                    Up to 10 targets can be submitted in one batch to the
                    existing Network and Web Scanner workflows.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={targetScanState.running}
                  onClick={() => setShowTargetScanPanel(false)}
                  aria-label="Close target scan review"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="csc-detail-drawer__body">
                <section className="csc-target-list-section">
                  <span>Selected targets</span>
                  <div className="csc-target-list">
                    {targetCandidates.slice(0, 10).map((candidate) => (
                      <label key={candidate.key}>
                        <input
                          type="checkbox"
                          checked={selectedTargetKeys.includes(candidate.key)}
                          disabled={targetScanState.running}
                          onChange={() =>
                            setSelectedTargetKeys((current) =>
                              current.includes(candidate.key)
                                ? current.filter((key) => key !== candidate.key)
                                : [...current, candidate.key]
                            )
                          }
                        />
                        <span className="csc-provider-badge">{candidate.provider}</span>
                        <strong>{candidate.type === "network" ? "Network" : "Web"}</strong>
                        <code>{candidate.target}</code>
                      </label>
                    ))}
                  </div>
                  {targetCandidates.length > 10 && (
                    <p>
                      {targetCandidates.length - 10} additional targets were
                      not selected. Run another reviewed batch after this one.
                    </p>
                  )}
                </section>

                <label className="csc-authorization-check">
                  <input
                    type="checkbox"
                    checked={targetAuthorization}
                    disabled={targetScanState.running}
                    onChange={(event) => setTargetAuthorization(event.target.checked)}
                  />
                  <span>
                    I confirm that I own these targets or have explicit written
                    authorization to scan them.
                  </span>
                </label>

                {(targetScanState.running || targetScanState.message) && (
                  <section className="csc-target-progress" aria-live="polite">
                    <span>Scan status</span>
                    <p>{targetScanState.message}</p>
                    <div className="csc-scan-progress__track">
                      <span
                        style={{
                          width: `${targetScanState.total
                            ? Math.round(
                                (targetScanState.completed /
                                  targetScanState.total) *
                                  100
                              )
                            : 0}%`,
                        }}
                      />
                    </div>
                    <small>
                      {targetScanState.completed}/{targetScanState.total} targets completed
                    </small>
                    {targetScanState.errors.map((error) => (
                      <p className="is-error" key={error.key}>
                        {error.target}: {error.message}
                      </p>
                    ))}
                  </section>
                )}
              </div>
              <div className="csc-detail-drawer__actions csc-target-drawer__actions">
                <button
                  type="button"
                  onClick={runDiscoveredTargetScans}
                  disabled={
                    targetScanState.running ||
                    !targetAuthorization ||
                    !selectedTargetKeys.length
                  }
                >
                  <FaNetworkWired />
                  {targetScanState.running ? "Scanning…" : "Start authorized scans"}
                </button>
                <button
                  type="button"
                  disabled={targetScanState.running}
                  onClick={() => setShowTargetScanPanel(false)}
                >
                  Close
                </button>
              </div>
            </aside>
          </div>
        )}

        {selectedFinding && (
          <div className="csc-detail-backdrop" role="presentation" onClick={() => setSelectedFinding(null)}>
            <aside
              className="csc-detail-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Cloud finding details"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="csc-detail-drawer__header">
                <div>
                  <span className="csc-provider-badge">{selectedFinding.provider}</span>
                  <span className={`csc-risk-badge csc-risk-badge--${selectedFinding.severity.toLowerCase()}`}>
                    {selectedFinding.severity}
                  </span>
                  <h2>{selectedFinding.findingName}</h2>
                  <p>{selectedFinding.findingType} · {selectedFinding.service}</p>
                </div>
                <button type="button" onClick={() => setSelectedFinding(null)} aria-label="Close finding details">
                  <FaTimes />
                </button>
              </div>
              <div className="csc-detail-drawer__body">
                <section>
                  <span>Why this was detected</span>
                  <p>{selectedFinding.rationale}</p>
                </section>
                <div className="csc-detail-metrics">
                  <div><span>Affected evidence</span><strong>{selectedFinding.flagged}</strong></div>
                  <div><span>CVSS</span><strong>{selectedFinding.cvss ?? "Not returned"}</strong></div>
                  <div><span>EPSS</span><strong>{selectedFinding.epss ?? "Not returned"}</strong></div>
                  <div><span>Exploit evidence</span><strong>{selectedFinding.exploitAvailable ?? "Not returned"}</strong></div>
                </div>
                <section>
                  <span>Recommended remediation</span>
                  <p>{selectedFinding.remediation}</p>
                </section>
                <section>
                  <span>Compliance impact</span>
                  {selectedFinding.compliance.length ? (
                    <div className="csc-chip-list">
                      {selectedFinding.compliance.map((control) => (
                        <i key={`${control.framework}-${control.control}`}>
                          {control.framework} {control.control}
                        </i>
                      ))}
                    </div>
                  ) : (
                    <p>No framework mapping was returned for this finding.</p>
                  )}
                </section>
                <section>
                  <span>Evidence provenance</span>
                  {selectedFinding.evidencePaths.length ? (
                    <code>{selectedFinding.evidencePaths.join("\n")}</code>
                  ) : (
                    <p>The scanner result identifies the target but returned no resource path.</p>
                  )}
                </section>
              </div>
              <div className="csc-detail-drawer__actions">
                <button type="button" onClick={() => askAiAboutFinding(selectedFinding)}>
                  <FaRobot /> Open AI analyst
                </button>
                <button type="button" onClick={() => exportTicketPayload(selectedFinding, "jira")}>
                  Export Jira payload
                </button>
                <button type="button" onClick={() => exportTicketPayload(selectedFinding, "servicenow")}>
                  Export ServiceNow payload
                </button>
              </div>
            </aside>
          </div>
        )}

        {selectedAsset && (
          <div className="csc-detail-backdrop" role="presentation" onClick={() => setSelectedAsset(null)}>
            <aside
              className="csc-detail-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Cloud asset details"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="csc-detail-drawer__header">
                <div>
                  <span className="csc-provider-badge">{selectedAsset.provider}</span>
                  <h2>{selectedAsset.name}</h2>
                  <p>{selectedAsset.service} · {selectedAsset.type}</p>
                </div>
                <button type="button" onClick={() => setSelectedAsset(null)} aria-label="Close asset details"><FaTimes /></button>
              </div>
              <div className="csc-detail-drawer__body">
                <div className="csc-detail-metrics">
                  <div><span>Region</span><strong>{selectedAsset.region}</strong></div>
                  <div><span>Exposure</span><strong>{selectedAsset.exposed ? "Public target observed" : "No public target observed"}</strong></div>
                  <div><span>SAVE score</span><strong>{selectedAssetProvider?.score === null || selectedAssetProvider?.score === undefined ? "Not calculated" : `${selectedAssetProvider.score.toFixed(1)}/10`}</strong></div>
                  <div><span>Status</span><strong>{selectedAssetFindings.length ? `${selectedAssetFindings.length} linked finding(s)` : "No linked finding"}</strong></div>
                </div>
                <section><span>Resource ID</span><code>{selectedAsset.resourceId}</code></section>
                <section><span>Public IPs</span><p>{selectedAsset.publicIps.join(", ") || "None returned"}</p></section>
                <section><span>HTTP endpoints</span><p>{selectedAsset.endpoints.join(", ") || "None returned"}</p></section>
                <section>
                  <span>Configuration, network, and web findings</span>
                  {selectedAssetFindings.length ? (
                    <div className="csc-linked-findings">
                      {selectedAssetFindings.map((finding) => (
                        <button
                          type="button"
                          key={finding.id}
                          onClick={() => {
                            setSelectedAsset(null);
                            setSelectedFinding(finding);
                          }}
                        >
                          <i className={`csc-risk-badge csc-risk-badge--${finding.severity.toLowerCase()}`}>
                            {finding.severity}
                          </i>
                          <span>{finding.findingName}</span>
                          <small>{finding.findingType}</small>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p>No finding is linked to this asset in the current evidence set.</p>
                  )}
                </section>
                <section><span>Last assessment</span><p>{formatAssessmentDate(selectedAssetProvider?.assessedAt)}</p></section>
                <section><span>Evidence path</span><code>{selectedAsset.evidencePath}</code></section>
              </div>
            </aside>
          </div>
        )}

        <footer className="csc-footer-note">
          <FaShieldAlt />
          Dashboard metrics come from existing SAVE cloud assessment records.
          Unconnected intelligence sources are never simulated.
        </footer>
      </main>
    </div>
  );
};

export default CloudSecurityCenter;
